/**
 * Skeleton Loading Components
 * Shows placeholder content while data is loading for better perceived performance
 */

export function SkeletonCard() {
  return (
    <div className="block p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 animate-pulse">
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
      <div className="flex gap-1.5 mt-3">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-16"></div>
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-20"></div>
      </div>
    </div>
  );
}

export function SkeletonList() {
  return (
    <div className="flex items-center gap-4 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 animate-pulse">
      <div className="flex-1">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
      </div>
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
    </div>
  );
}

export function SkeletonSidebar() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden animate-pulse">
      {/* Teams Section */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50">
          <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
        </div>
        <div className="p-2 space-y-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="px-2 py-2 flex justify-between">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-6"></div>
            </div>
          ))}
        </div>
      </div>
      {/* Categories Section */}
      <div>
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50">
          <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
        </div>
        <div className="p-2 space-y-1">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="px-2 py-2 flex justify-between">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-6"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface SkeletonPageProps {
  gridView?: boolean;
}

export function SkeletonPage({ gridView = true }: SkeletonPageProps) {
  return (
    <div id="ssd-app-loaded" className="min-h-screen dark">
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header Skeleton */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-40 animate-pulse hidden sm:block"></div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <SkeletonSidebar />
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {/* Search Bar Skeleton */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
                <div className="flex gap-2">
                  <div className="h-10 w-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                  <div className="h-10 w-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
              </div>

              {/* Results Count Skeleton */}
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4 animate-pulse"></div>

              {/* Grid/List Skeleton */}
              {gridView ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <SkeletonList key={i} />
                  ))}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SkeletonLoader({ className }: { className?: string }) {
  return (
    <div className={`bg-gray-200 dark:bg-gray-700 animate-pulse ${className}`}></div>
  );
}
